import React, { useEffect, useState, useCallback } from "react";
import { listUsers, updateUser } from "../../api/adminApi";

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const TIERS = ["basic", "pro", "plus", "advisor"];

const AdminUsers = () => {
  const [data, setData] = useState({ results: [], count: 0, page: 1, page_size: 25 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await listUsers({ q, page }));
    } catch {
      setError("Couldn't load users.");
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Optimistically patch a single row, roll back on failure.
  const patchRow = async (id, patch) => {
    setError("");
    const prev = data.results;
    setData((d) => ({
      ...d,
      results: d.results.map((u) => (u.id === id ? { ...u, ...patch } : u)),
    }));
    try {
      await updateUser(id, patch);
    } catch {
      setError("Update failed — reverted.");
      setData((d) => ({ ...d, results: prev }));
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const totalPages = Math.max(1, Math.ceil((data.count || 0) / (data.page_size || 25)));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Users</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {data.count} total. Change a user's tier, toggle staff access, or disable an account.
        </p>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 max-w-md">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search username or email…"
          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
        />
        <button type="submit" className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500">
          Search
        </button>
      </form>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-500/10 text-red-500 text-sm">{error}</div>
      )}

      <Card className="overflow-x-auto">
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : data.results.length === 0 ? (
          <p className="text-sm text-gray-500">No users found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-widest">
                <th className="py-2 pr-3">User</th>
                <th className="py-2 px-3">Tier</th>
                <th className="py-2 px-3">Advisor</th>
                <th className="py-2 px-3">Staff</th>
                <th className="py-2 px-3">Active</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((u) => (
                <tr key={u.id} className="border-t border-gray-200 dark:border-white/5">
                  <td className="py-3 pr-3">
                    <div className="font-medium">{u.username}</div>
                    <div className="text-xs text-gray-500">{u.email || "—"}</div>
                  </td>
                  <td className="py-3 px-3">
                    <select
                      value={u.tier}
                      onChange={(e) => patchRow(u.id, { tier: e.target.value })}
                      className="px-2 py-1 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 text-sm capitalize"
                    >
                      {TIERS.map((t) => (
                        <option key={t} value={t} className="capitalize">{t}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 px-3">
                    {u.is_advisor ? (
                      <span className="text-emerald-500 text-xs font-semibold">Yes</span>
                    ) : (
                      <span className="text-gray-400 text-xs">No</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <input
                      type="checkbox"
                      checked={u.is_staff}
                      onChange={(e) => patchRow(u.id, { is_staff: e.target.checked })}
                      className="w-4 h-4 accent-indigo-600"
                    />
                  </td>
                  <td className="py-3 px-3">
                    <input
                      type="checkbox"
                      checked={u.is_active}
                      onChange={(e) => patchRow(u.id, { is_active: e.target.checked })}
                      className="w-4 h-4 accent-indigo-600"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/15 text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {data.page} of {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/15 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
